/*
 * One glyph per product, drawn in the mark's flavour.
 *
 * WHY THIS IS DATA AND NOT THREE SETS OF ICONS
 *
 * The same eight products are rendered on three surfaces — the public page, the Apps page in
 * the viewer, and the Apps sidebar submenu — and each one grew its own icons independently:
 * hand-drawn SVG on the marketing page, nothing at all on the Apps page, and Material-UI stock
 * icons in the submenu. Nobody chose that. It is what three PRs solving three surfaces produces
 * when the icon has no owner, and it is the same shape as every other drift this repository has
 * had to test its way out of: the palette in five files, the counts in seven places, the status
 * bullet that existed twice saying different things.
 *
 * So the glyph is defined once, here, as primitives rather than as markup or as a component.
 * Primitives can be rendered by React for the viewer AND compared against the static HTML the
 * marketing page hand-writes — which is the only way to tie in a page that deliberately has no
 * framework and cannot import anything at run time.
 *
 * `role` is `line` or `node`, matching the two stroke weights the mark uses. Colour is never
 * here: the surfaces carry their own palette, and a hex in this file would be the second
 * definition of a colour that `tokens.ts` already owns.
 *
 * It lives beside `products.ts` rather than in `packages/branding` — which owns the mark — for
 * a practical reason: the scaffolded Backstage app is a separate yarn project and cannot
 * resolve a workspace package, which is why the overlay's logos are copied files rather than
 * imports. Two of the three consumers are here; the third is checked against this by test.
 */

export type GlyphRole = "line" | "node";

export interface GlyphPrimitive {
  readonly tag: "rect" | "circle" | "line" | "path";
  readonly role: GlyphRole;
  /*
   * The attribute set genuinely differs by shape — a circle has `cx`/`cy`/`r`, a rect has
   * `x`/`y`/`width`/`height` — so TypeScript unions them across the array and gives every
   * branch the others' keys as `?: undefined`. The type says `| undefined` because that is
   * what the data is, rather than casting each element to hide it. React drops an undefined
   * attribute, so an absent key never reaches the DOM.
   */
  readonly attrs: Readonly<Record<string, string | undefined>>;
}

/** The 24x24 grid every glyph is drawn on. */
export const GLYPH_VIEWBOX = "0 0 24 24";

export const PRODUCT_GLYPHS: Readonly<Record<string, readonly GlyphPrimitive[]>> = Object.freeze({
  Catalog: [
    { tag: "rect", role: "line", attrs: { x: "4", y: "3.5", width: "16", height: "17", rx: "2" } },
    { tag: "circle", role: "node", attrs: { cx: "8", cy: "8", r: "1.3" } },
    { tag: "line", role: "line", attrs: {  } },
    { tag: "line", role: "line", attrs: {  } },
    { tag: "line", role: "line", attrs: {  } },
  ],
  Graph: [
    { tag: "line", role: "line", attrs: {  } },
    { tag: "line", role: "line", attrs: {  } },
    { tag: "line", role: "line", attrs: {  } },
    { tag: "circle", role: "node", attrs: { cx: "6", cy: "6.5", r: "2.3" } },
    { tag: "circle", role: "line", attrs: { cx: "18", cy: "10", r: "2.3" } },
    { tag: "circle", role: "line", attrs: { cx: "11.5", cy: "17.5", r: "2.3" } },
  ],
  Discovery: [
    { tag: "circle", role: "line", attrs: { cx: "10.5", cy: "10.5", r: "6.5" } },
    { tag: "line", role: "line", attrs: {  } },
    { tag: "circle", role: "node", attrs: { cx: "10.5", cy: "10.5", r: "1.8" } },
  ],
  Live: [
    { tag: "path", role: "line", attrs: { d: "M3 13 H8 L10 7 L13.5 17.5 L15.5 12 H21" } },
    { tag: "circle", role: "node", attrs: { cx: "10", cy: "7", r: "1.5" } },
  ],
  Blueprint: [
    { tag: "rect", role: "line", attrs: { x: "3.5", y: "4.5", width: "17", height: "15", rx: "2" } },
    { tag: "rect", role: "line", attrs: { x: "6.5", y: "8", width: "5", height: "4.5", rx: "1" } },
    { tag: "line", role: "line", attrs: {  } },
    { tag: "circle", role: "node", attrs: { cx: "16.5", cy: "10.2", r: "1.4" } },
    { tag: "line", role: "line", attrs: {  } },
  ],
  Connect: [
    { tag: "rect", role: "line", attrs: { x: "3", y: "9", width: "10", height: "6", rx: "3" } },
    { tag: "rect", role: "line", attrs: { x: "11", y: "9", width: "10", height: "6", rx: "3" } },
    { tag: "circle", role: "node", attrs: { cx: "12", cy: "12", r: "1.4" } },
  ],
  Recover: [
    { tag: "path", role: "line", attrs: { d: "M12 3.5 L18.5 6 V11 L12 19.5 L5.5 11 V6 Z" } },
    { tag: "circle", role: "node", attrs: { cx: "12", cy: "10.5", r: "2.2" } },
  ],
  Guide: [
    { tag: "path", role: "line", attrs: { d: "M3.5 5 h17 v10.5 h-8.5 l-4.5 3.5 v-3.5 h-4 Z" } },
    { tag: "circle", role: "node", attrs: { cx: "12", cy: "10.25", r: "2.2" } },
  ],
});

/** Every product that has a glyph. A product without one is a gap, not a default. */
export function glyphFor(product: string): readonly GlyphPrimitive[] | null {
  return PRODUCT_GLYPHS[product] ?? null;
}
